import { useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { galleryItems, GALLERY_CATS } from "@/data/content";
import { fetchPublicProjects } from "@/lib/api";

interface PublicProject {
  id: string;
  title: string;
  city: string;
  category: string;
  desc_en: string;
  desc_es: string;
  photos: { url: string }[];
}

export const Gallery = () => {
  const { t, lang } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [projects, setProjects] = useState<PublicProject[]>([]);

  useEffect(() => {
    fetchPublicProjects()
      .then((r) => setProjects(r.data.projects || []))
      .catch(() => setProjects([]));
  }, []);

  const visible = galleryItems.filter((g) => filter === "all" || g.cat === filter);
  const visibleProjects = projects.filter((p) => filter === "all" || p.category === filter);
  const hasProjects = projects.length > 0;
  const cats = [...GALLERY_CATS, "dirtWork", "cleanup", "demolition", "hauling"].filter(
    (c) => hasProjects || GALLERY_CATS.includes(c),
  );

  return (
    <section
      id="work"
      data-testid="work-section"
      className="bg-[#0d0d0d] py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <Reveal>
              <SectionLabel text={t.work.label} testId="work-label" />
              <h2
                data-testid="work-heading"
                className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
              >
                {t.work.heading}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4">
            <Reveal delay={0.15}>
              <p className="text-base leading-relaxed text-neutral-400">{t.work.sub}</p>
              <p
                data-testid="work-placeholder-note"
                className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-neutral-600"
              >
                {t.work.note}
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="work-filters"
            className="mt-12 flex flex-wrap gap-2.5 md:mt-16"
          >
            {["all", ...cats].map((cat) => (
              <button
                key={cat}
                data-testid={`work-filter-${cat}`}
                onClick={() => setFilter(cat)}
                className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  filter === cat
                    ? "border-gold bg-gold text-ink"
                    : "border-white/15 text-neutral-400 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {cat === "all" ? t.work.all : t.work.cats[cat]}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visibleProjects.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <figure
                data-testid={`project-card-${p.id}`}
                className="group relative overflow-hidden border border-white/10 bg-charcoal"
              >
                {p.photos[0] && (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.photos[0].url}
                      alt={`${p.title} — ${t.work.cats[p.category] || p.category}, R3Stan Drywall Houston`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                )}
                <figcaption className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-display text-lg font-black tracking-tight text-white">
                      {p.title}
                    </p>
                    <span className="shrink-0 border border-white/10 bg-ink/70 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-gold">
                      {t.work.cats[p.category] || p.category}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                    {p.city}
                    {p.photos.length > 1 && ` · ${p.photos.length} photos`}
                  </p>
                  {(lang === "es" ? p.desc_es || p.desc_en : p.desc_en) && (
                    <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                      {lang === "es" ? p.desc_es || p.desc_en : p.desc_en}
                    </p>
                  )}
                  {p.photos.length > 1 && (
                    <div className="mt-4 flex gap-2">
                      {p.photos.slice(1, 5).map((ph, j) => (
                        <img
                          key={j}
                          src={ph.url}
                          alt={`${p.title} photo ${j + 2}`}
                          loading="lazy"
                          className="h-12 w-16 border border-white/10 object-cover"
                        />
                      ))}
                    </div>
                  )}
                </figcaption>
              </figure>
            </Reveal>
          ))}
          {visible.map((item, i) => (
            <Reveal key={`${item.cat}-${i}`} delay={(i % 3) * 0.08}>
              {item.comingSoon || !item.img ? (
                <div
                  data-testid={`work-tile-${item.cat}-placeholder`}
                  className="flex aspect-[4/3] flex-col items-center justify-center gap-3 border border-dashed border-white/15 bg-charcoal p-6 text-center"
                >
                  <Camera className="h-7 w-7 text-neutral-600" strokeWidth={1.5} />
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-300">
                    {t.work.cats[item.cat]}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                    {t.work.comingSoon}
                  </p>
                </div>
              ) : (
                <figure
                  data-testid={`work-tile-${i}`}
                  className="group relative aspect-[4/3] overflow-hidden border border-white/10 bg-charcoal"
                >
                  <img
                    src={item.img}
                    alt={`${t.work.cats[item.cat]} — R3Stan Drywall, Houston drywall contractor`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-ink/20 transition-colors duration-500 group-hover:bg-ink/0" />
                  <figcaption className="absolute left-4 top-4 border border-white/10 bg-ink/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-md">
                    {t.work.cats[item.cat]}
                  </figcaption>
                </figure>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
